import { LiquidGlass } from "apple-liquid-glass-webgl";

// One optical material for every lens. Ratios are relative to each
// surface's short side, so the same values hold from the phone pill
// to a tall open menu.
const MATERIAL = {
  refraction: 104,
  edgeReach: 0.24,
  edgeWidth: 0.22,
  dispersion: 1.15,
  rim: 0.62,
  reflection: 0.42,
  highlight: 0.68,
  hairline: 1,
  hairWidth: 0.55,
  echo: 0.2,
  body: 0.42,
  absorption: 0.28,
  lightAngle: 124,
  backdropBlur: 4,
  frost: 0,
};

const NAV_TINT = 0.37;
const MOBILE_MENU_QUERY = "(max-width: 768px)";

function surfaceOptions(tint = NAV_TINT) {
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.matchMedia("(max-width: 760px)").matches;
  return {
    tint,
    tintTone: "dark",
    frost: 0,
    maxDpr: coarse || narrow ? 1.5 : 2,
    // The OS setting is handled here: no lens at all, so the solid bar in
    // CSS is what shows. The library's own reduced mode still draws glass.
    respectReducedTransparency: false,
    material: MATERIAL,
  };
}

// Mounts the nav pill, and each desktop menu as it opens. The mobile
// sheet grows the pill itself, so it stays on the same lens.
function attach(shell) {
  const menus = new Map();
  const mobile = window.matchMedia(MOBILE_MENU_QUERY);
  let nav = null;

  const sync = () => {
    const sheetOpen = mobile.matches && Boolean(shell.querySelector(".bt-mobile-menu"));
    if (sheetOpen) {
      nav?.destroy();
      nav = null;
    } else if (!nav) {
      nav = new LiquidGlass(shell, surfaceOptions());
    }

    const live = mobile.matches ? new Set() : new Set(shell.querySelectorAll(".bt-nav-menu"));
    for (const [el, glass] of menus) {
      if (live.has(el)) continue;
      glass.destroy();
      menus.delete(el);
    }
    for (const el of live) {
      if (!menus.has(el)) menus.set(el, new LiquidGlass(el, surfaceOptions()));
    }
  };

  sync();
  const observer = new MutationObserver(sync);
  observer.observe(shell, { childList: true, subtree: true });
  mobile.addEventListener("change", sync);

  return () => {
    mobile.removeEventListener("change", sync);
    observer.disconnect();
    nav?.destroy();
    for (const glass of menus.values()) glass.destroy();
    menus.clear();
  };
}

// Reduced transparency is the solid bar. The refractive lens is what shows
// otherwise, and it follows the setting if it changes without a reload.
export function mountLiquidGlass(shell) {
  if (!shell) return () => {};

  const reduce = window.matchMedia("(prefers-reduced-transparency: reduce)");
  let detach = () => {};

  const apply = () => {
    detach();
    detach = reduce.matches ? () => {} : attach(shell);
  };

  apply();
  reduce.addEventListener("change", apply);
  return () => {
    reduce.removeEventListener("change", apply);
    detach();
  };
}
